// src/types/automation.types.ts
// Types for the configurable automation/reminders system

import { Timestamp } from 'firebase/firestore';
import { TaskType, TaskPriority } from './task.types';

// ============================================
// TRIGGER TYPES
// ============================================

export type AutomationTriggerEvent =
  | 'lead_created'
  | 'lead_assigned'
  | 'lead_status_changed'
  | 'lead_determination_changed'
  | 'appointment_created'
  | 'appointment_status_changed';

export type ScheduleRelativeTo =
  | 'appointment_date'
  | 'lead_created'
  | 'last_contact';

export interface AutomationSchedule {
  type: 'relative' | 'fixed';
  relativeTo?: ScheduleRelativeTo;
  offsetDays?: number; // -1 = day before, 1 = day after
  offsetHours?: number;
  time?: string; // "HH:mm" format for when to run
  timezone?: string;
}

export interface AutomationTrigger {
  type: 'event' | 'scheduled';
  event?: AutomationTriggerEvent;
  schedule?: AutomationSchedule;
}

// ============================================
// CONDITIONS
// ============================================

export type ConditionOperator =
  | 'equals'
  | 'not_equals'
  | 'greater_than'
  | 'less_than'
  | 'greater_than_or_equals'
  | 'less_than_or_equals'
  | 'contains'
  | 'in'
  | 'not_in'
  | 'is_empty'
  | 'is_not_empty';

export interface AutomationCondition {
  id: string;
  field: string; // e.g., 'appointment.status', 'daysSince.leadCreated'
  operator: ConditionOperator;
  value: any;
}

// ============================================
// ACTIONS
// ============================================

export type ActionType =
  | 'create_task'
  | 'send_notification'
  | 'send_email';

export type AssignToType =
  | 'role'
  | 'specific_user'
  | 'triggered_user'    // user who triggered the event
  | 'assigned_user'     // user assigned to the lead
  | 'captured_by'       // user who captured the lead
  | 'client_user';      // client_user on the project

export interface AssignToConfig {
  type: AssignToType;
  role?: string | string[]; // 'provider_user', 'client_user', etc.
  userId?: string;
}

export interface TaskActionConfig {
  type: TaskType;
  titleTemplate: string; // Supports variables like {{lead.parentName}}
  descriptionTemplate?: string;
  priority: TaskPriority;
  dueDaysFromTrigger: number;
  dueTime?: string; // "HH:mm" format
}

export interface NotificationActionConfig {
  titleTemplate: string;
  messageTemplate: string;
  channels: ('in_app' | 'email')[];
  emailTemplate?: string;
}

export interface AutomationAction {
  id: string;
  type: ActionType;
  taskConfig?: TaskActionConfig;
  notificationConfig?: NotificationActionConfig;
  assignTo: AssignToConfig;
}

// ============================================
// AUTOMATION RULE
// ============================================

export interface ExecutionStats {
  totalExecutions: number;
  successfulExecutions: number;
  failedExecutions: number;
  lastExecutedAt?: Timestamp;
}

export interface AutomationRule {
  id: string;
  organizationId?: string;
  name: string;
  description: string;
  enabled: boolean;
  isTemplate: boolean;
  templateId?: string;

  trigger: AutomationTrigger;
  conditions: AutomationCondition[];
  conditionLogic: 'and' | 'or';
  actions: AutomationAction[];

  projectScope: 'all' | 'specific';
  projectIds?: string[];

  executionStats: ExecutionStats;

  createdBy: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  updatedBy: string;
}

// ============================================
// EXECUTION TRACKING
// ============================================

export interface ActionExecutionResult {
  actionId: string;
  actionType: ActionType;
  status: 'success' | 'failed';
  resultId?: string; // task ID or notification ID created
  error?: string;
}

export interface AutomationExecution {
  id: string;
  ruleId: string;
  ruleName: string;
  triggerType: string;
  triggerEvent?: string;
  triggerData: Record<string, any>;
  status: 'success' | 'failed' | 'partial';
  actionsExecuted: ActionExecutionResult[];
  executedAt: Timestamp;
  projectId?: string;
}

// ============================================
// DTOs FOR CREATING/UPDATING
// ============================================

export interface CreateAutomationRuleDto {
  name: string;
  description: string;
  enabled?: boolean;
  organizationId?: string;
  trigger: AutomationTrigger;
  conditions: Omit<AutomationCondition, 'id'>[];
  conditionLogic?: 'and' | 'or';
  actions: Omit<AutomationAction, 'id'>[];
  projectScope: 'all' | 'specific';
  projectIds?: string[];
  templateId?: string;
}

export interface UpdateAutomationRuleDto {
  name?: string;
  description?: string;
  enabled?: boolean;
  trigger?: AutomationTrigger;
  conditions?: Omit<AutomationCondition, 'id'>[];
  conditionLogic?: 'and' | 'or';
  actions?: Omit<AutomationAction, 'id'>[];
  projectScope?: 'all' | 'specific';
  projectIds?: string[];
}

// ============================================
// TEMPLATES
// ============================================

export interface ConfigurableField {
  path: string; // e.g., "trigger.schedule.time", "actions.0.taskConfig.priority"
  label: string;
  type: 'number' | 'string' | 'select' | 'time' | 'roles' | 'boolean';
  defaultValue: any;
  options?: { label: string; value: any }[];
  required?: boolean;
  description?: string;
}

export interface AutomationTemplate {
  id: string;
  name: string;
  description: string;
  category: 'lead' | 'appointment' | 'follow_up' | 'alert';
  trigger: AutomationTrigger;
  conditions: Omit<AutomationCondition, 'id'>[];
  actions: Omit<AutomationAction, 'id'>[];
  configurableFields: ConfigurableField[];
}

// ============================================
// UI HELPER TYPES
// ============================================

export interface ConditionFieldOption {
  value: string;
  label: string;
  category: 'lead' | 'appointment' | 'user' | 'computed';
  operators: ConditionOperator[];
  valueType: 'string' | 'number' | 'boolean' | 'date' | 'select';
  options?: { label: string; value: any }[];
}

// Available condition fields for the UI
export const CONDITION_FIELDS: ConditionFieldOption[] = [
  // Lead fields
  { value: 'lead.status', label: 'Lead Status', category: 'lead', operators: ['equals', 'not_equals', 'in'], valueType: 'select', options: [
    { label: 'Uncontacted', value: 'uncontacted' },
    { label: 'Contacted', value: 'contacted' },
    { label: 'Scheduled', value: 'scheduled' },
    { label: 'Enrolled', value: 'enrolled' },
    { label: 'Not Interested', value: 'not_interested' },
    { label: 'Invalid', value: 'invalid' }
  ]},
  { value: 'lead.determination', label: 'Lead Determination', category: 'lead', operators: ['equals', 'not_equals'], valueType: 'select', options: [
    { label: 'Pending', value: 'pending' },
    { label: 'Valid', value: 'valid' },
    { label: 'Invalid', value: 'invalid' }
  ]},

  // Appointment fields
  { value: 'appointment.status', label: 'Appointment Status', category: 'appointment', operators: ['equals', 'not_equals'], valueType: 'select', options: [
    { label: 'Scheduled', value: 'scheduled' },
    { label: 'Completed', value: 'completed' },
    { label: 'Cancelled', value: 'cancelled' },
    { label: 'No-Show', value: 'no-show' }
  ]},
  { value: 'appointment.eventType', label: 'Appointment Type', category: 'appointment', operators: ['equals', 'not_equals', 'in'], valueType: 'select', options: [
    { label: 'Tour', value: 'tour' },
    { label: 'Phone', value: 'phone' },
    { label: 'Meeting', value: 'meeting' }
  ]},

  // Computed fields
  { value: 'daysSince.leadCreated', label: 'Days Since Lead Created', category: 'computed', operators: ['greater_than', 'less_than', 'equals'], valueType: 'number' },
  { value: 'daysSince.lastContact', label: 'Days Since Last Contact', category: 'computed', operators: ['greater_than', 'less_than', 'equals'], valueType: 'number' }
];

// Available trigger events for the UI
export const TRIGGER_EVENTS: { value: AutomationTriggerEvent; label: string; description: string }[] = [
  { value: 'lead_created', label: 'Lead Created', description: 'When a new lead is captured' },
  { value: 'lead_assigned', label: 'Lead Assigned', description: 'When a lead is assigned to a user' },
  { value: 'lead_status_changed', label: 'Lead Status Changed', description: 'When a lead status is updated' },
  { value: 'lead_determination_changed', label: 'Lead Determination Changed', description: 'When a lead is marked valid/invalid' },
  { value: 'appointment_created', label: 'Appointment Created', description: 'When an appointment is scheduled' },
  { value: 'appointment_status_changed', label: 'Appointment Status Changed', description: 'When appointment status changes (completed, no-show, etc.)' }
];
